import { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.jsx';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { setPolicyNumber } from '@/services/deals.service.js';

// The policy number, in the deal header beside the fees (David 2026-10-30:
// "There is no visible place to enter a policy number on a bound deal").
// Writes through the policy writer only (setPolicyNumber → policy-info door).
//
// A CONFIRMATION IS A READ. The door writes the policies row and the Deal at
// once, but the deal page reads the pipeline, which rebuilds from Zoho behind
// a stale-while-revalidate cache: measured 2026-10-30 on N820T, the deal read
// answered no number at +0 s and +4 s and the number at +12 s. So after a save
// the key shows the number as pending, re-reads the deal until the read agrees
// (then says so), and if it has not agreed in RECHECK_MS it says the number is
// saved and shows on the next reload. It never claims the screen shows what it
// does not.
const RECHECK_EVERY_MS = 3000;
const RECHECK_MS = 30000;

export default function DealPolicyNumberKey({ dealId, policyNumber, onChange }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [awaiting, setAwaiting] = useState(null); // the saved number the deal has not re-read yet
  const timers = useRef([]);
  const awaitingRef = useRef(null); // the same value, readable from a timer
  useEffect(() => { awaitingRef.current = awaiting; }, [awaiting]);
  useEffect(() => { if (open) setValue(policyNumber || ''); }, [open, policyNumber]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // The read agreed: say so once, stop re-reading.
  useEffect(() => {
    if (!awaiting || policyNumber !== awaiting) return;
    timers.current.forEach(clearTimeout); timers.current = [];
    toast.success(`Policy # ${awaiting} is on the policy (read back from the deal)`);
    setAwaiting(null);
  }, [policyNumber, awaiting]);

  const typed = value.trim();
  const base = 'inline-flex items-center gap-1 rounded px-1 py-px text-xs';

  async function save() {
    if (!typed || typed === (policyNumber || '')) return;
    setBusy(true);
    try {
      const res = await setPolicyNumber(dealId, typed);
      if (res?.success === false) throw new Error(res?.error || 'failed');
      setOpen(false);
      setAwaiting(typed);
      timers.current.forEach(clearTimeout);
      timers.current = [];
      for (let t = 0; t < RECHECK_MS; t += RECHECK_EVERY_MS) timers.current.push(setTimeout(() => onChange?.(), t));
      timers.current.push(setTimeout(() => {
        const still = awaitingRef.current;
        if (!still) return;
        toast.info(`Policy # ${still} is saved to the policy; the deal page has not re-read it yet, so it shows on the next reload.`);
        setAwaiting(null);
      }, RECHECK_MS));
    } catch (err) {
      toast.error(`Policy number not saved: ${err?.body?.error || err?.message || 'failed'}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={policyNumber ? 'Change this policy\'s number' : 'Enter the carrier\'s policy number for this policy'}
          data-deal-policy-number={awaiting ? 'pending' : policyNumber ? 'set' : 'missing'}
          className={`${base} hover:bg-surface-strong ${policyNumber || awaiting ? 'font-mono text-text' : 'font-medium text-accent'}`}
        >
          {awaiting ? `Policy # ${awaiting} (saved, re-reading the deal)` : policyNumber ? `Policy # ${policyNumber}` : 'Add policy number'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="bottom" sideOffset={4} className="w-80 p-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-dim">Policy number</div>
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-2">
          <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="As printed on the binder or policy" className="font-mono" autoFocus />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" size="sm" disabled={busy || !typed || typed === (policyNumber || '')}>
              {busy ? 'Saving…' : policyNumber ? `Change to ${typed || '…'}` : `Save ${typed || 'number'}`}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}
